import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Panel } from '@rtkwlf/ui';
import { Breadcrumb, BreadcrumbItem } from 'reactstrap';
import { APIError } from '../../../../../APIError';
import { PageTitle } from '../../../../../widgets/PageTitle';
import Viewport from '../../../../../widgets/Viewport';
import ViewHeader from '../../../../../widgets/ViewHeader';
import { OnboardingScannerForm } from './Form';
import { onboardingSelector } from '../../../../../reducers/onboarding';
import ErrorJumbotron from '../../../../../widgets/ErrorJumbotron';
import LoadingIndicator from '../../../../../widgets/LoadingIndicator';
import { HTTP_STATUS, ROUTE_PATH } from '../../../../../constants';
import {
  EMPTY_SCANNER,
  getFormValues,
  getScannerModelFromFormValues,
  ScannerFormValues,
} from './constants';
import { ONBOARDING_ACTIONS } from '../../../../../actions/onboarding';
import { apiRequest } from '../../../../../APIClient';
import { Formik } from 'formik';
import { createSensorScannerValidationSchema } from '../../../validationSchemas';
import { ScrollSectionKeys } from '../../OnboardingSiteConfig';

const NOT_FOUND = new APIError('Scanner not found', HTTP_STATUS.NOT_FOUND);

const ErrorPanel = styled(Panel)`
  margin-left: 0;
  margin-right: 0;
  min-height: 225px;
`;

/**
 * This view should guard against scenarios where the scanner is not editable
 *  e.g. still loading, not found
 *  and render an error banner instead of the form
 */
export const OnboardingScannerEditView: React.FunctionComponent = () => {
  const isLoading = useSelector(onboardingSelector.getScannersSectionLoading);

  const { uuid } = useParams<{ uuid: string }>();
  const scanner = useSelector(onboardingSelector.getScannerByUuid(uuid));

  const dispatch = useDispatch();

  const defaultScannerValues: ScannerFormValues = scanner
    ? getFormValues(scanner)
    : EMPTY_SCANNER;

  const onSubmitScanner = React.useCallback(
    (values: ScannerFormValues) => {
      const scanner = getScannerModelFromFormValues(values);
      dispatch(ONBOARDING_ACTIONS.EDIT_SCANNER(uuid, scanner));
      dispatch(apiRequest.saveOBScanners());
    },
    [dispatch, uuid],
  );

  let errorContent: React.ReactNode;

  if (isLoading) {
    errorContent = (
      <LoadingIndicator size="lg" centered>
        Loading scanner...
      </LoadingIndicator>
    );
  } else if (!scanner) {
    errorContent = <ErrorJumbotron error={NOT_FOUND} />;
  }

  if (errorContent) {
    return (
      <Viewport>
        <Breadcrumb>
          <BreadcrumbItem>
            <Link to={ROUTE_PATH.ONBOARDING.MAIN}>Onboarding Overview</Link>
          </BreadcrumbItem>
          <BreadcrumbItem active>Edit Scanner</BreadcrumbItem>
        </Breadcrumb>
        <PageTitle title="Edit Scanner" />
        <ViewHeader title="Edit Scanner" />

        <ErrorPanel className="min-height-for-loading-indicator">
          {errorContent}
        </ErrorPanel>
      </Viewport>
    );
  }

  const content = errorContent ? (
    <ErrorPanel className="min-height-for-loading-indicator">
      {errorContent}
    </ErrorPanel>
  ) : (
    <Formik
      initialValues={defaultScannerValues}
      onSubmit={onSubmitScanner}
      validationSchema={createSensorScannerValidationSchema()}
    >
      <OnboardingScannerForm view={ScrollSectionKeys.SCANNERS} />
    </Formik>
  );

  const title = scanner ? 'Edit Scanner - ' + scanner.name : 'Edit Scanner';

  return (
    <Viewport>
      <Breadcrumb>
        <BreadcrumbItem>
          <Link to={ROUTE_PATH.ONBOARDING.MAIN}>Onboarding Overview</Link>
        </BreadcrumbItem>
        <BreadcrumbItem active>Edit Scanner</BreadcrumbItem>
      </Breadcrumb>
      <PageTitle title={title} />
      <ViewHeader title={title} />
      {content}
    </Viewport>
  );
};
